import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import services from 'services';

const PromptBlockerContext = React.createContext({
  message: '',
  handleDirtyCheck: () => {},
  handleMessagePrompt: () => {},
});

export const usePromptBlocker = () => React.useContext(PromptBlockerContext);

export const LeavePageBlocker = ({ when, message }) => {
  const { handleMessagePrompt } = usePromptBlocker();

  useEffect(() => {
    if (services.expired || !when) return () => {};

    const beforeUnloadCallback = (event) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', beforeUnloadCallback);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadCallback);
    };
  }, [when, message]);

  return <Prompt when={when} message={message || handleMessagePrompt} />;
};

const PromptBlockerProvider = ({ children }) => {
  //! State
  const { t } = useTranslation();
  const message = t('shared:change_not_saved');

  //! Function
  const handleDirtyCheck = React.useCallback(
    ({ isDirty, onPass } = {}) => {
      if (isDirty) {
        const isOk = window.confirm(message);
        if (!isOk) {
          return;
        }
      }

      onPass && onPass();
    },
    [message]
  );

  const handleMessagePrompt = React.useCallback(
    (location, action) => {
      const currentLocation = window.location.pathname;
      const nextLocation = location.pathname;

      if (action === 'POP') {
        return message;
      }

      if (nextLocation !== currentLocation) {
        return message;
      }

      return true;
    },
    [message]
  );

  const valueExport = useMemo(() => {
    return {
      message,
      handleDirtyCheck,
      handleMessagePrompt,
    };
  }, [message, handleDirtyCheck, handleMessagePrompt]);

  //! Render
  return (
    <PromptBlockerContext.Provider value={valueExport}>{children}</PromptBlockerContext.Provider>
  );
};

export default PromptBlockerProvider;
